import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Mail, Key, Eye, EyeOff, Shield, Building, User, ArrowLeft, CheckCircle, Loader, AlertCircle } from 'lucide-react';
import zxcvbn from 'zxcvbn';
import { 
  signInWithEmail, 
  signUpWithEmail, 
  resetPassword as resetPasswordFn 
} from '../lib/auth';
import { getBrowserCapabilities } from '../lib/supabase';
import { PasswordResetModal } from '../components/PasswordResetModal';

function AuthPage() {
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [browserInfo, setBrowserInfo] = useState<any>(null);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  useEffect(() => {
    // Check browser capabilities
    const capabilities = getBrowserCapabilities();
    setBrowserInfo(capabilities);
    
    // Check if there's a session already
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // Get user role and redirect accordingly
          const role = session.user?.user_metadata?.role || 'user';
          
          if (role === 'admin') {
            navigate('/admin');
          } else if (role === 'landlord') {
            navigate('/landlord');
          } else {
            navigate('/matches');
          }
        }
      } catch (error) {
        console.error('Error checking session:', error);
      }
    };
    
    checkSession();
  }, [navigate]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const checkPasswordStrength = (password: string) => {
    const result = zxcvbn(password.trim());
    setPasswordStrength(result.score);
    return result.score >= 3;
  };

  const getPasswordRequirements = () => {
    if (!showPasswordRequirements) return null;

    const requirements = [
      { met: password.length >= 8, text: 'At least 8 characters' },
      { met: /[A-Z]/.test(password), text: 'At least one uppercase letter' },
      { met: /[a-z]/.test(password), text: 'At least one lowercase letter' },
      { met: /[0-9]/.test(password), text: 'At least one number' },
      { met: /[^A-Za-z0-9]/.test(password), text: 'At least one special character' }
    ];

    return (
      <div className="mt-2 text-sm">
        <p className="font-medium text-gray-700 mb-1">Password requirements:</p>
        <ul className="space-y-1">
          {requirements.map((req, index) => (
            <li key={index} className="flex items-center">
              <span className={`mr-2 ${req.met ? 'text-green-500' : 'text-gray-400'}`}>
                {req.met ? '✓' : '○'}
              </span>
              <span className={req.met ? 'text-green-700' : 'text-gray-600'}>
                {req.text}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const getPasswordFeedback = () => {
    if (!password) return null;

    const strengthColors = ['red', 'orange', 'yellow', 'lime', 'green'];
    const strengthTexts = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
    
    return (
      <div className="mt-1">
        <div className="flex gap-1 mb-1">
          {[0, 1, 2, 3, 4].map((score) => (
            <div
              key={score}
              className={`h-2 w-full rounded-full ${
                score <= passwordStrength
                  ? `bg-${strengthColors[passwordStrength]}-500`
                  : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        <p className={`text-xs text-${strengthColors[passwordStrength]}-600`}>
          {strengthTexts[passwordStrength]}
        </p>
      </div>
    );
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const { success, error } = await resetPasswordFn(email.trim());

      if (!success) throw new Error(error || 'Failed to send reset instructions');

      setResetSent(true);
      setSuccessMessage(
        'Password reset instructions have been sent to your email. Please check your inbox and follow the instructions to reset your password.'
      );
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    
    // Validate email
    if (!validateEmail(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    // Additional validation for signup
    if (isSignUp) {
      if (!checkPasswordStrength(trimmedPassword)) {
        setError('Please choose a stronger password. Use a mix of letters, numbers, and symbols.');
        setShowPasswordRequirements(true);
        return;
      }

      if (trimmedPassword !== confirmPassword.trim()) {
        setError('Passwords do not match');
        return;
      }
    }

    setLoading(true);

    try {
      if (isSignUp) {
        const { data, error } = await signUpWithEmail(trimmedEmail, trimmedPassword);

        if (error) {
          if (error.includes('User already registered')) {
            throw new Error('An account with this email already exists. Please sign in instead.');
          }
          throw new Error(error);
        }

        navigate('/onboarding');
      } else {
        const { data, error } = await signInWithEmail(trimmedEmail, trimmedPassword, rememberMe);

        if (error) {
          if (error.toLowerCase().includes('invalid_credentials') || 
              error.toLowerCase().includes('invalid login credentials')) {
            throw new Error('The password you entered is incorrect. Please try again or use the "Forgot password?" link to reset it.');
          }
          throw new Error(error);
        }

        if (!data?.session) {
          throw new Error('No session created. Please try again.');
        }

        // Get user role and redirect accordingly
        const { data: { user } } = await supabase.auth.getUser();
        const role = user?.user_metadata?.role || 'user';
        
        if (role === 'admin') {
          navigate('/admin');
        } else if (role === 'landlord') {
          navigate('/landlord');
        } else {
          navigate('/matches');
        }
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      if (!isSignUp) {
        setPassword('');
      }
      setConfirmPassword('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-12 px-4">
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold text-center mb-8">
          {isSignUp ? 'Create Your Account' : 'Welcome Back'}
        </h1>

        <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg">
          {error && (
            <div className="mb-6 p-4 text-sm text-red-700 bg-red-100 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {successMessage && (
            <div className="mb-6 p-4 text-sm text-green-700 bg-green-100 rounded-lg flex items-start gap-2">
              <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}

          {browserInfo && !browserInfo.localStorage && (
            <div className="mb-6 p-4 text-sm text-yellow-700 bg-yellow-100 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <span>Your browser has localStorage disabled. This may affect login persistence.</span>
            </div>
          )}

          <form id="auth-form" onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="you@example.com"
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Key className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (isSignUp) {
                      checkPasswordStrength(e.target.value);
                      setShowPasswordRequirements(true);
                    }
                  }}
                  onFocus={() => {
                    if (isSignUp) setShowPasswordRequirements(true);
                  }}
                  className="pl-10 pr-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="••••••••"
                  autoComplete={isSignUp ? "new-password" : "current-password"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {isSignUp && getPasswordFeedback()}
              {isSignUp && getPasswordRequirements()}
            </div>

            {isSignUp && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Key className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />
                </div>
              </div>
            )}

            {!isSignUp && (
              <div className="flex items-center">
                <input
                  id="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center">
                  <Loader className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" />
                  {isSignUp ? 'Creating Account...' : 'Signing In...'}
                </span>
              ) : (
                isSignUp ? 'Create Account' : 'Sign In'
              )}
            </button>

            <div className="flex flex-col space-y-2">
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setError(null);
                  setSuccessMessage(null);
                  setPassword('');
                  setConfirmPassword('');
                  setShowPasswordRequirements(false);
                }}
                className="w-full text-center text-sm text-indigo-600 hover:text-indigo-500"
              >
                {isSignUp ? 'Already have an account? Sign in' : 'Need an account? Sign up'}
              </button>

              {!isSignUp && (
                <button
                  type="button"
                  onClick={() => setShowPasswordResetModal(true)}
                  className="w-full text-center text-sm text-gray-600 hover:text-gray-500 font-medium"
                >
                  Forgot your password?
                </button>
              )}
            </div>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-gray-600">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>

      {/* Password Reset Modal */}
      <PasswordResetModal 
        isOpen={showPasswordResetModal}
        onClose={() => setShowPasswordResetModal(false)}
        email={email}
      />
    </div>
  );
}

export default AuthPage;